import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  CHAT_ATTACHMENT_BUCKET,
  CONVERSATION_MEDIA_BUCKET,
  MESSAGE_PAGE_SIZE,
  type ChatAttachment,
  type ChatConversation,
  type ChatMember,
  type ChatMessage,
  type ChatMessageType,
  type ChatUser,
  type ConversationMemberRole,
  type ConversationPurpose,
  type ConversationType,
} from "@/lib/chat/types";
import { asObject, createId, safeStorageExtension } from "@/lib/utils";

type ConversationListRow = {
  id: string;
  conversation_type: ConversationType;
  title: string | null;
  avatar_path: string | null;
  updated_at: string;
  my_role: ConversationMemberRole;
  last_read_at: string | null;
  unread_count: number;
  last_message_id: string | null;
  last_message_body: string | null;
  last_message_type: ChatMessageType | null;
  last_message_at: string | null;
  last_message_sender_id: string | null;
  last_message_deleted: boolean | null;
  allows_video: boolean | null;
  members: unknown;
  purpose: ConversationPurpose;
  service_name: string | null;
  closed_at: string | null;
};

type MessageRow = {
  id: string;
  conversation_id: string;
  sender_id: string;
  message_type: ChatMessageType;
  body: string | null;
  attachment_data: Record<string, unknown> | null;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  sender: { full_name: string; avatar_path?: string | null } | { full_name: string; avatar_path?: string | null }[] | null;
  reply_to_message_id: string | null;
};

type ReplyMessageRow = {
  id: string;
  body: string | null;
  deleted_at: string | null;
  sender_id: string;
};

function mapMembers(value: unknown): ChatMember[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    const row = item as Record<string, unknown>;
    if (typeof row.user_id !== "string" || typeof row.full_name !== "string") {
      return [];
    }
    return [
      {
        userId: row.user_id,
        role: (row.role as ConversationMemberRole) ?? "member",
        fullName: row.full_name,
        professionalTitle:
          typeof row.professional_title === "string"
            ? row.professional_title
            : null,
        avatarPath:
          typeof row.avatar_path === "string" ? row.avatar_path : null,
      },
    ];
  });
}

function conversationDisplayName(
  type: ConversationType,
  title: string | null,
  members: ChatMember[],
  currentUserId: string,
) {
  if (type === "group" || type === "community") {
    return title || "Csoportos beszélgetés";
  }

  const other = members.find((member) => member.userId !== currentUserId);
  return other?.fullName ?? "Privát beszélgetés";
}

async function signConversationAvatars(paths: (string | null)[]) {
  const unique = [...new Set(paths.filter((path): path is string => Boolean(path)))];
  const urls = new Map<string, string>();
  if (unique.length === 0) {
    return urls;
  }

  const supabase = getSupabaseBrowserClient();
  const { data } = await supabase.storage
    .from(CONVERSATION_MEDIA_BUCKET)
    .createSignedUrls(unique, 60 * 60);

  for (const item of data ?? []) {
    if (item.path && item.signedUrl) {
      urls.set(item.path, item.signedUrl);
    }
  }
  return urls;
}

function parseAttachment(
  data: Record<string, unknown> | null,
): Omit<ChatAttachment, "url"> | null {
  if (!data) {
    return null;
  }
  const bucket = data.storage_bucket;
  const path = data.storage_path;
  if (typeof bucket !== "string" || typeof path !== "string") {
    return null;
  }

  return {
    bucket,
    path,
    fileName: typeof data.file_name === "string" ? data.file_name : "Csatolmány",
    mimeType: typeof data.mime_type === "string" ? data.mime_type : "",
    sizeBytes: typeof data.size_bytes === "number" ? data.size_bytes : 0,
  };
}

async function signAttachment(
  attachment: Omit<ChatAttachment, "url"> | null,
): Promise<ChatAttachment | null> {
  if (!attachment) {
    return null;
  }

  const supabase = getSupabaseBrowserClient();
  const { data } = await supabase.storage
    .from(attachment.bucket)
    .createSignedUrl(attachment.path, 60 * 60);

  return { ...attachment, url: data?.signedUrl ?? null };
}

function mapMessage(
  row: MessageRow,
  attachment: ChatAttachment | null,
  reply: ReplyMessageRow | null,
): ChatMessage {
  const sender = asObject(row.sender);
  return {
    id: row.id,
    conversationId: row.conversation_id,
    senderId: row.sender_id,
    senderName: sender?.full_name ?? "Ismeretlen felhasználó",
    senderAvatarPath:
      typeof sender?.avatar_path === "string" ? sender.avatar_path : null,
    messageType: row.message_type,
    body: row.body,
    createdAt: row.created_at,
    editedAt: row.edited_at,
    deletedAt: row.deleted_at,
    attachment,
    replyTo: reply
      ? {
          id: reply.id,
          senderName: "Korábbi üzenet",
          body: reply.body,
          deleted: Boolean(reply.deleted_at),
        }
      : null,
  };
}

const MESSAGE_SELECT = `
  id,
  conversation_id,
  sender_id,
  message_type,
  body,
  attachment_data,
  created_at,
  edited_at,
  deleted_at,
  reply_to_message_id,
  sender:profiles!messages_sender_id_fkey (full_name, avatar_path)
`;

async function getReplyMessageMap(rows: MessageRow[]) {
  const ids = [
    ...new Set(
      rows
        .map((row) => row.reply_to_message_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const replies = new Map<string, ReplyMessageRow>();

  if (ids.length === 0) {
    return replies;
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("messages")
    .select("id, body, deleted_at, sender_id")
    .in("id", ids);

  if (error) {
    throw error;
  }

  for (const row of (data ?? []) as ReplyMessageRow[]) {
    replies.set(row.id, row);
  }

  return replies;
}

export async function listMyConversations(
  currentUserId: string,
): Promise<ChatConversation[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("list_my_conversations");
  if (error) {
    throw error;
  }

  const rows = (data ?? []) as ConversationListRow[];
  const avatarUrls = await signConversationAvatars(
    rows.map((row) => row.avatar_path),
  );

  return rows.map((row) => {
    const members = mapMembers(row.members);
    return {
      id: row.id,
      type: row.conversation_type,
      title: row.title,
      avatarPath: row.avatar_path,
      avatarUrl: row.avatar_path
        ? avatarUrls.get(row.avatar_path) ?? null
        : null,
      updatedAt: row.updated_at,
      myRole: row.my_role,
      lastReadAt: row.last_read_at,
      unreadCount: row.unread_count,
      lastMessage:
        row.last_message_id && row.last_message_type && row.last_message_at
          ? {
              id: row.last_message_id,
              body: row.last_message_body,
              type: row.last_message_type,
              createdAt: row.last_message_at,
              senderId: row.last_message_sender_id ?? "",
              deleted: Boolean(row.last_message_deleted),
            }
          : null,
      members,
      displayName:
        row.purpose === "service" && row.service_name
          ? `${row.service_name} · ${conversationDisplayName(
              row.conversation_type,
              row.title,
              members,
              currentUserId,
            )}`
          : conversationDisplayName(
              row.conversation_type,
              row.title,
              members,
              currentUserId,
            ),
      canManage:
        row.my_role === "owner" || row.my_role === "admin",
      allowsVideo: Boolean(row.allows_video) && row.conversation_type === "direct",
      purpose: row.purpose,
      serviceName: row.service_name,
      closedAt: row.closed_at,
      isClosed: Boolean(row.closed_at),
    };
  });
}

export async function getConversationMessages(input: {
  conversationId: string;
  before?: string;
}): Promise<ChatMessage[]> {
  const supabase = getSupabaseBrowserClient();
  let query = supabase
    .from("messages")
    .select(MESSAGE_SELECT)
    .eq("conversation_id", input.conversationId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(MESSAGE_PAGE_SIZE);

  if (input.before) {
    query = query.lt("created_at", input.before);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  const rows = (data ?? []) as unknown as MessageRow[];
  const replyMessages = await getReplyMessageMap(rows);
  const mapped = await Promise.all(
    rows.map(async (row) =>
      mapMessage(
        row,
        await signAttachment(parseAttachment(row.attachment_data)),
        row.reply_to_message_id
          ? replyMessages.get(row.reply_to_message_id) ?? null
          : null,
      ),
    ),
  );

  return mapped.reverse();
}

export async function getMessageById(messageId: string): Promise<ChatMessage | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("messages")
    .select(MESSAGE_SELECT)
    .eq("id", messageId)
    .maybeSingle();

  if (error) {
    throw error;
  }
  if (!data) {
    return null;
  }

  const row = data as unknown as MessageRow;
  const replyMessages = await getReplyMessageMap([row]);
  return mapMessage(
    row,
    await signAttachment(parseAttachment(row.attachment_data)),
    row.reply_to_message_id
      ? replyMessages.get(row.reply_to_message_id) ?? null
      : null,
  );
}

export async function searchChatUsers(search: string): Promise<ChatUser[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("search_chat_users", {
    search_query: search,
  });
  if (error) {
    throw error;
  }

  return ((data ?? []) as {
    id: string;
    full_name: string;
    professional_title: string | null;
    avatar_path: string | null;
    practice_name: string | null;
  }[]).map((row) => ({
    id: row.id,
    fullName: row.full_name,
    professionalTitle: row.professional_title,
    avatarPath: row.avatar_path,
    practiceName: row.practice_name,
  }));
}

export async function createDirectConversation(otherUserId: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_direct_conversation", {
    other_user_id: otherUserId,
  });
  if (error) {
    throw error;
  }
  return data as string;
}

export async function createGroupConversation(input: {
  title: string;
  memberIds: string[];
  avatarPath: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_group_conversation", {
    group_title: input.title,
    member_ids: input.memberIds,
    group_avatar_path: input.avatarPath,
  });
  if (error) {
    throw error;
  }
  return data as string;
}

export async function addConversationMembers(input: {
  conversationId: string;
  memberIds: string[];
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("add_conversation_members", {
    target_conversation_id: input.conversationId,
    member_ids: input.memberIds,
  });
  if (error) {
    throw error;
  }
}

export async function removeConversationMember(input: {
  conversationId: string;
  userId: string;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("remove_conversation_member", {
    target_conversation_id: input.conversationId,
    target_user_id: input.userId,
  });
  if (error) {
    throw error;
  }
}

export async function updateGroupConversation(input: {
  conversationId: string;
  title: string;
  avatarPath?: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("update_group_conversation", {
    target_conversation_id: input.conversationId,
    group_title: input.title,
    group_avatar_path: input.avatarPath ?? null,
  });
  if (error) {
    throw error;
  }
}

export async function markConversationRead(input: {
  conversationId: string;
  userId: string;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase
    .from("conversation_members")
    .update({ last_read_at: new Date().toISOString() })
    .eq("conversation_id", input.conversationId)
    .eq("user_id", input.userId);
  if (error) {
    throw error;
  }
}

export async function sendChatMessage(input: {
  conversationId: string;
  senderId: string;
  body: string;
  messageType: ChatMessageType;
  replyToMessageId?: string | null;
  attachment?: Omit<ChatAttachment, "url"> | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const trimmed = input.body.trim();
  const { error } = await supabase.from("messages").insert({
    conversation_id: input.conversationId,
    sender_id: input.senderId,
    message_type: input.messageType,
    body: trimmed || null,
    reply_to_message_id: input.replyToMessageId ?? null,
    attachment_data: input.attachment
      ? {
          storage_bucket: input.attachment.bucket,
          storage_path: input.attachment.path,
          file_name: input.attachment.fileName,
          mime_type: input.attachment.mimeType,
          size_bytes: input.attachment.sizeBytes,
        }
      : {},
  });

  if (error) {
    throw error;
  }
}

export async function deleteOwnMessage(messageId: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase
    .from("messages")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", messageId);
  if (error) {
    throw error;
  }
}

export async function setServiceConversationClosed(input: {
  conversationId: string;
  closed: boolean;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("set_service_conversation_closed", {
    target_conversation_id: input.conversationId,
    next_closed: input.closed,
  });
  if (error) {
    throw error;
  }
}

export async function uploadChatAttachment(input: {
  userId: string;
  conversationId: string;
  file: File;
}): Promise<Omit<ChatAttachment, "url">> {
  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name);
  const path = `${input.userId}/${input.conversationId}/${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(CHAT_ATTACHMENT_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return {
    bucket: CHAT_ATTACHMENT_BUCKET,
    path,
    fileName: input.file.name,
    mimeType: input.file.type,
    sizeBytes: input.file.size,
  };
}

export async function removeChatAttachment(path: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage
    .from(CHAT_ATTACHMENT_BUCKET)
    .remove([path]);
  return error;
}

export async function uploadConversationAvatar(input: {
  userId: string;
  file: File;
}) {
  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name, "jpg");
  const path = `${input.userId}/${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(CONVERSATION_MEDIA_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });
  if (error) {
    throw error;
  }
  return path;
}

export async function removeConversationAvatar(path: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage
    .from(CONVERSATION_MEDIA_BUCKET)
    .remove([path]);
  return error;
}
